import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useParams } from "react-router-dom";
import Cookies from "js-cookie";

import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { getProviderPerformanceReview } from "api";
import { renderAddressV2InfoForHeaders } from "helpers/addressV2Helpers";
import Button from "components/atoms/Button/Button";
import Avatar from "components/v2/Avatar/Avatar";
import Star from "pages/common/ShipmentBilling/components/Star/Star";
import RateNowDialog from "pages/common/ShipmentBilling/components/RateNowDialog/RateNowDialog";
import usePermissions from "hooks/usePermissions";
import ConsolidatedModal from "pages/shipper/ShipmentQuotes/components/ConsolidatedModal";
import testIds from "constants/testIds";
import TopNavLocationDetail from "../QuickQuotePage/TopNavLocationDetail";

function TopNavTrackingPage() {
  const { id } = useParams();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";

  const { data: shipmentBasicData } = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const isParentShipment = shipmentBasicData?.related_shipments?.some(
    (item: any) => item?.relation?.toLowerCase() === "child",
  );

  const { data: shipmentDetails = {} } = useGetShipmentDetails(id!, Cookies.get("is_guest") !== "true", {
    enabled: !!id && !isParentShipment,
  }) as any;

  const [showRateDialog, setShowRateDialog] = useState(false);
  const [showConsolidatedModal, setShowConsolidatedModal] = useState(false);
  const [isLoadingPerformanceRating, setIsLoadingPerformanceRating] = useState(false);
  const [providerPerformanceRating, setProviderPerformanceRating] = useState<number | null>(null);

  const hasShipmentDetails = !!Object.keys(shipmentDetails || {}).length;
  const schedule = shipmentDetails?.schedule || {};
  const organization = shipmentDetails?.organization || {};
  const { quick_request_details } = shipmentDetails || {};

  const providerCompany = organization.provider_company;
  const providerLogo = organization.provider_logo;
  const shipperCompany = organization.shipper_company;
  const shipperLogo = organization.shipper_logo;
  const handledByCompany = isShipper ? providerCompany : shipperCompany;
  const handledByCompanyLogo = isShipper ? providerLogo : shipperLogo;

  const shipmentStatus = organization.shipment_status;
  const shipmentId = organization.shipment_id || id;
  const isOffPlatform = shipmentBasicData?.shipment?.off_platform;

  const { address_v2 } = schedule;
  const hasAddressV2 = Object.keys(address_v2 ?? {}).some(
    (key) => key !== "pickup_is_port_address" && key !== "delivery_is_port_address",
  );
  const { renderPickupAddress, renderDeliveryAddress } = renderAddressV2InfoForHeaders({
    data: shipmentDetails,
    useRevampedDesign: true,
  });

  // Fetch provider performance rating for delivered shipments
  useEffect(() => {
    if (isShipper && shipmentStatus === "Delivered" && !showRateDialog && Cookies.get("is_guest") !== "true" && shipmentId) {
      setIsLoadingPerformanceRating(true);
      getProviderPerformanceReview(shipmentId)
        .then((res: any) => setProviderPerformanceRating(res.data.overall_performance_rating))
        .finally(() => setIsLoadingPerformanceRating(false));
    }
  }, [showRateDialog, shipmentStatus, shipmentId, isShipper]);

  if (!hasShipmentDetails) return null;

  const hasDetails = handledByCompany || schedule.pickup_address_name || schedule.stop1_address_name;
  if (!hasDetails) return null;

  const pickupCopyFields = isOffPlatform
    ? [schedule.pickup_address_name, schedule.pickup_city, schedule.pickup_country]
    : [
        schedule.pickup_address_name,
        schedule.pickup_addressline1,
        schedule.pickup_addressline2,
        schedule.pickup_postal_code,
        schedule.pickup_city,
        schedule.pickup_country,
      ];

  const deliveryCopyFields = isOffPlatform
    ? [schedule.stop1_address_name, schedule.stop1_city, schedule.stop1_country]
    : [
        schedule.stop1_address_name,
        schedule.stop1_addressline1,
        schedule.stop1_addressline2,
        schedule.stop1_postal_code,
        schedule.stop1_city,
        schedule.stop1_country,
      ];

  return (
    <>
      <RateNowDialog
        id={shipmentId}
        isOpen={showRateDialog}
        close={() => {
          setShowRateDialog(false);
        }}
      />
      <ConsolidatedModal
        data={quick_request_details}
        isOpen={showConsolidatedModal}
        close={() => setShowConsolidatedModal(false)}
      />
      <div className="topnav-v2__details" data-testid={testIds.TOPNAV_V2_TRACKING_DETAILS}>
        {/* Handled by / Requested by */}
        {handledByCompany && (
          <div
            className="topnav-v2__detail-col topnav-v2__handled-by-col"
            data-testid={testIds.TOPNAV_V2_TRACKING_HANDLED_BY_CONTAINER}
          >
            <Avatar
              name={handledByCompany}
              size="md"
              image={handledByCompanyLogo === "/static/img/logo.png" ? undefined : handledByCompanyLogo}
            />
            <div className="topnav-v2__handled-by-info" data-testid={testIds.TOPNAV_V2_TRACKING_HANDLED_BY_INFO}>
              <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_TRACKING_HANDLED_BY_LABEL}>
                {isShipper ? (
                  <FormattedMessage id="tracking.handledBy" defaultMessage="Handled by" />
                ) : (
                  <FormattedMessage id="tracking.requestedBy" defaultMessage="Requested by" />
                )}
              </span>
              <span className="topnav-v2__handled-by-name" data-testid={testIds.TOPNAV_V2_TRACKING_HANDLED_BY_VALUE}>
                {handledByCompany}
              </span>
              {isShipper &&
                shipmentStatus === "Delivered" &&
                !isLoadingPerformanceRating &&
                Cookies.get("is_guest") !== "true" &&
                !shipmentDetails?.viewing_as_guest_user &&
                (providerPerformanceRating ? (
                  <Link
                    to="#"
                    data-testid={testIds.TOPNAV_V2_TRACKING_RATE_LINK}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRateDialog(true);
                    }}
                  >
                    <Star color="#FDB833" count={providerPerformanceRating} size="sm" />
                  </Link>
                ) : (
                  <Button
                    variant="link"
                    className="topnav-v2__rate-btn"
                    data-testid={testIds.TOPNAV_V2_TRACKING_RATE_BUTTON}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setShowRateDialog(true);
                    }}
                  >
                    <FormattedMessage id="billing.rate" defaultMessage="Rate" />
                  </Button>
                ))}
              {quick_request_details && quick_request_details.length > 1 && (
                <Button
                  variant="link"
                  className="topnav-v2__rate-btn"
                  onClick={() => setShowConsolidatedModal(true)}
                  data-testid={testIds.TOPNAV_V2_TRACKING_VIEW_CONSOLIDATED_ORDERS_BUTTON}
                >
                  <FormattedMessage id="general.viewConsolidatedOrders" defaultMessage="View consolidated orders" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Pickup */}
        {schedule.pickup_address_name && (
          <TopNavLocationDetail
            type="pickup"
            addressName={schedule.pickup_address_name}
            addressFields={
              isOffPlatform
                ? [schedule.pickup_city, schedule.pickup_country]
                : [
                    schedule.pickup_addressline1,
                    schedule.pickup_addressline2,
                    schedule.pickup_postal_code,
                    schedule.pickup_city,
                    schedule.pickup_country,
                  ]
            }
            copyFields={pickupCopyFields}
            date={schedule.pickup_date}
            time={schedule.pickup_time}
            endDate={schedule.pickup_end_date}
            endTime={schedule.pickup_end_time}
            hasAddressV2={hasAddressV2}
            renderAddressV2={renderPickupAddress}
            automationIdPrefix="topnav-v2-tracking-pickup"
          />
        )}

        {/* Delivery */}
        {schedule.stop1_address_name && (
          <TopNavLocationDetail
            type="delivery"
            addressName={schedule.stop1_address_name}
            addressFields={
              isOffPlatform
                ? [schedule.stop1_city, schedule.stop1_country]
                : [
                    schedule.stop1_addressline1,
                    schedule.stop1_addressline2,
                    schedule.stop1_postal_code,
                    schedule.stop1_city,
                    schedule.stop1_country,
                  ]
            }
            copyFields={deliveryCopyFields}
            date={schedule.stop1_date}
            time={schedule.stop1_time}
            endDate={schedule.stop1_end_date}
            endTime={schedule.stop1_end_time}
            hasAddressV2={hasAddressV2}
            renderAddressV2={renderDeliveryAddress}
            automationIdPrefix="topnav-v2-tracking-delivery"
          />
        )}
      </div>
    </>
  );
}

export default TopNavTrackingPage;
